import { Box } from "@mui/material";
import type { PropsWithChildren } from "react";
import {
  type ModalProps,
  ReactionsListModal,
  type ReactionsListModalProps,
  WithComponents,
} from "stream-chat-react";
import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import { Resources, useResource } from "Translations/Resources";

/**
 * Stream's modal shell swapped for the app's dialog. Stream anchors its close button to the
 * viewport corner — the spot the floating support button occupies on every page — so the
 * reactions detail had no visible way to close. ResponsiveDialog puts the close button in the
 * card (and is a bottom sheet on mobile).
 *
 * The content is Stream's and reads its CSS variables from a `.str-chat` ancestor, which the
 * dialog's portal to <body> leaves behind — hence the scoping wrapper with the chat root's classes.
 */
const ReactionsDialog = ({
  open,
  onClose,
  children,
}: PropsWithChildren<ModalProps>) => {
  const { t } = useResource();

  return (
    <ResponsiveDialog
      isOpen={open}
      // Stream's handler wants the closing event; the app dialog has none to give, and the
      // reactions modal ignores it anyway — an Escape keydown is the honest stand-in.
      onClose={() => onClose?.(new KeyboardEvent("keydown", { key: "Escape" }))}
      title={t(Resources.Chat.Reactions.Title)}
    >
      <Box
        className="str-chat messaging light"
        display="flex"
        flexDirection="column"
        minHeight={240}
        maxHeight="60vh"
      >
        {children}
      </Box>
    </ResponsiveDialog>
  );
};

/** The reactions detail (who reacted with what) — Stream's list inside the app's dialog. */
export const ChatReactionsListModal = (props: ReactionsListModalProps) => (
  <WithComponents overrides={{ Modal: ReactionsDialog }}>
    <ReactionsListModal {...props} />
  </WithComponents>
);
